import React, { useCallback } from "react";
import RequiredFieldIndicator from "@components/ui/indicators/RequiredFieldIndicator";
import { useTranslation } from "react-i18next";
import { t_help } from "@i18n/usualTexts/t_help";

export interface SimpleSelectProps {
  name: string;
  onChange: (value: string, name: string) => void;
  value: string;
  options: string[];
  children: string;

  errors?: Set<string>;
  required?: boolean;
  submitted?: boolean;
  toPresentErrors?: boolean;
  autofocus?: boolean;
}

const validateRequiredState = (
  errors: Set<string>,
  submitted: boolean,
  required: boolean,
  value: string,
  requiredMsg: string,
): void => {
  if (submitted && required && !value) {
    errors.add(requiredMsg);
  } else if (value && required) {
    errors.delete(requiredMsg);
  }
};

const SimpleSelect: React.FC<SimpleSelectProps> = ({
  name,
  onChange,
  value,
  options,
  children,
  errors = new Set(),
  required = false,
  submitted = false,
  toPresentErrors = true,
  autofocus = false,
}) => {
  const { t } = useTranslation();
  const requiredMsg = t(t_help.fieldOrSelect, { placeholder: children });

  validateRequiredState(errors, submitted, required, value, requiredMsg);

  const hasErrors = errors.size > 0;
  const borderClass = hasErrors ? "border-red-500" : "border-[var(--border-medium)]";

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = e.target.value;
      if (!selectedValue || !options.includes(selectedValue)) return;
      onChange(selectedValue, name);
    },
    [onChange, name, options],
  );

  return (
    <div>
      <label
        className={`block text-sm leading-tight mb-1.5 ${hasErrors ? "text-red-600" : "text-[var(--text-primary)]"}`}
        htmlFor={name}
      >
        {children}
        <RequiredFieldIndicator required={required} className="ml-0.5" />
      </label>

      <select
        className={`auth-input w-full h-10 px-3 py-2 text-sm bg-[var(--surface-primary)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)] transition-colors duration-200 border ${borderClass} appearance-none cursor-pointer`}
        name={name}
        id={name}
        value={value}
        required={required}
        autoFocus={autofocus}
        onChange={handleChange}
        aria-describedby={hasErrors ? `${name}-error` : undefined}
      >
        <option value="" disabled hidden>
          {children}
        </option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>

      {hasErrors && toPresentErrors && (
        <>
          {[...errors].map((error) => (
            <div
              key={error}
              id={`${name}-error`}
              data-testid="spanElement"
              className={`text-xs text-red-500 mt-1${submitted ? " animate-pulse" : ""}`}
              role="alert"
              aria-live="polite"
            >
              {error}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default SimpleSelect;
